import { toNumber } from './decimal'
import { formatEuros } from './format'
import { formatHmm } from './hours'
import type { WeekCell, WeekRow } from './types'

/** Wat er verdwijnt als deze regel wordt verwijderd. */
export interface DeleteStake {
  entryIds: string[]
  hours: number
  travel: number
  /** Alleen de dagen waar uren op staan, in weekvolgorde. */
  days: { dayIndex: number; hours: number }[]
  /** Getypt maar nog niet opgeslagen: er staat dan nog niets in de week zelf. */
  draftOnly: boolean
  /** Hoe ver de knop reikt: de hele weekregel, of alleen de dag die in beeld staat. */
  scope: 'week' | 'day'
}

export type RowDeleteAction =
  | { kind: 'blocked'; reason: 'locked' | 'saving' }
  | { kind: 'local' }
  | { kind: 'direct'; entryIds: string[] }
  | { kind: 'confirm'; stake: DeleteStake }

/**
 * Wat de prullenbak op een regel moet doen. Bevestigen zodra er uren of reiskosten
 * sneuvelen; staat alles op nul, dan verdwijnt de regel zonder vragen.
 *
 * De knop reikt zover als je kijkt. In de weekweergave staan alle zeven dagen van de
 * regel voor je, dus wist hij de weekregel; in de dagweergave zie je er één, dus krijgt
 * hij `dayIndex` mee en blijft de rest van de week staan.
 *
 * Reiskosten tellen mee omdat de server ze mee verwijdert (`TimeEntryService::delete`),
 * en `draftHours` telt mee omdat je die uren wel degelijk in het veld ziet staan, ook al
 * kent de server ze nog niet.
 *
 * `saved` dekt hetzelfde gat als in `rowMenuState`: tussen de eerste opslag en het
 * serverantwoord bestaat de entry al terwijl de regel nog geen entry-ids heeft. Zonder
 * die guard haalt de prullenbak de lege huls weg en staat de regel na de refetch gewoon
 * weer terug, dus verwijderen dat stilzwijgend niets doet.
 */
export function rowDeleteAction(row: WeekRow, saved: boolean, draftHours = 0, dayIndex?: number): RowDeleteAction {
  const scope: DeleteStake['scope'] = dayIndex === undefined ? 'week' : 'day'
  const cells = dayIndex === undefined ? row.cells : [row.cells[dayIndex]]

  if (cells.some((cell) => cell.locked)) return { kind: 'blocked', reason: 'locked' }

  const entryIds = cells.map((cell) => cell.entryId).filter((id): id is string => id !== null)
  if (entryIds.length === 0) {
    if (saved) return { kind: 'blocked', reason: 'saving' }
    if (draftHours <= 0) return { kind: 'local' }
    return { kind: 'confirm', stake: { entryIds: [], hours: draftHours, travel: 0, days: [], draftOnly: true, scope } }
  }

  const hours = sum(cells, (cell) => toNumber(cell.hours))
  const travel = sum(cells, (cell) => toNumber(cell.travelAmount))
  if (hours <= 0 && travel <= 0) return { kind: 'direct', entryIds }

  return {
    kind: 'confirm',
    stake: {
      entryIds,
      hours,
      travel,
      days: cells
        .filter((cell) => toNumber(cell.hours) > 0)
        .map((cell) => ({ dayIndex: cell.dayIndex, hours: toNumber(cell.hours) })),
      draftOnly: false,
      scope,
    },
  }
}

/** Of de prullenbak in de celpopover eerst om bevestiging vraagt. */
export function cellNeedsConfirm(cell: WeekCell): boolean {
  return toNumber(cell.hours) > 0 || toNumber(cell.travelAmount) > 0
}

/**
 * De vocabulaire van het verwijderen, op één plek zodat de dialoog, de knop in de
 * celpopover en de melding achteraf dezelfde woorden voeren. Het woord is overal
 * "verwijderd" en "Verwijder"; "gewist" bestaat niet meer.
 */
export const deleteCopy = {
  title: 'Regel verwijderen',
  confirm: 'Verwijder',
  pending: 'Verwijderen…',
  cancel: 'Annuleren',
  saving: 'Even wachten, de regel wordt opgeslagen',
  locked: 'Gefactureerd, niet te verwijderen',
} as const

/**
 * De gevolgen, in de description van de dialoog. Bij een weekregel staat de week er
 * letterlijk in, want die knop reikt verder dan wat je op dat moment ziet. Bij een
 * dagregel gaat het over die ene dag en zou de week juist te veel beloven.
 */
export function deleteDescription(stake: DeleteStake, label: string, weekLabel: string, dayName: (dayIndex: number) => string): string {
  const amount = formatHmm(stake.hours)
  if (stake.draftOnly) return `Je hebt ${amount} ingevuld op ${label} die nog niet is opgeslagen.`
  if (stake.scope === 'day') {
    if (stake.days.length === 0) return `${label} wordt verwijderd.`
    return `${amount} op ${label} wordt verwijderd op ${dayName(stake.days[0].dayIndex)}.`
  }
  if (stake.days.length === 1) {
    return `${amount} op ${label} wordt verwijderd uit ${weekLabel}, op ${dayName(stake.days[0].dayIndex)}.`
  }
  if (stake.days.length === 0) return `${label} wordt verwijderd uit ${weekLabel}.`
  return `${amount} op ${label} wordt verwijderd uit ${weekLabel}, verdeeld over ${stake.days.length} dagen.`
}

/** De uitsplitsing per dag, of null als er niets te waarschuwen valt. */
export function deleteDayBreakdown(stake: DeleteStake, dayShort: (dayIndex: number) => string): string | null {
  if (stake.days.length < 2) return null
  return stake.days.map((day) => `${dayShort(day.dayIndex)} ${formatHmm(day.hours)}`).join(' · ')
}

/** Geld dat mee verdwijnt hoort genoemd te worden, ook als het niet de aanleiding is. */
export function deleteTravelNote(stake: DeleteStake): string | null {
  return stake.travel > 0 ? `Ook ${formatEuros(stake.travel)} reiskosten verdwijnt.` : null
}

function sum<T>(items: T[], value: (item: T) => number): number {
  return items.reduce((total, item) => total + value(item), 0)
}
